// @ts-check
// Peças comuns das telas de integrações (contrato 08): trecho de código com copiar, selo de status, campo de texto
// com rótulo e aviso para quem não é administrador.
import { html } from 'htm/preact';
import { Button } from '../../ui/button.js';
import { Icon } from '../../ui/icon.js';
import { showSuccessSnackbar } from '../../ui/snackbar.js';
import { copyToClipboard } from './settings-channels-page.js';
import { ApiError, getErrorMessage } from '../../data/api-client.js';
import { t } from '../../i18n/index.js';

/**
 * @param {{ code: string, label: string }} props
 */
export function CodeSnippet({ code, label }) {
  return html`<div class="integration-snippet">
    <div class="integration-snippet-header">
      <span class="form-field-label">${label}</span>
      <${Button}
        title=${t('leadCapture.common.copy')}
        icon="IconCopy"
        variant="secondary"
        size="small"
        onClick=${async () => {
          if (await copyToClipboard(code)) showSuccessSnackbar(t('leadCapture.common.copied'));
        }}
      />
    </div>
    <pre class="integration-snippet-code"><code>${code}</code></pre>
  </div>`;
}

const STATUS_COLORS = /** @type {Record<string, string>} */ ({
  SUCCEEDED: 'green', APPROVED: 'green', ACTIVE: 'green',
  PENDING: 'yellow', ENQUEUED: 'yellow', RETRYING: 'orange', IN_APPEAL: 'orange',
  FAILED: 'red', REJECTED: 'red', DISABLED: 'gray', PAUSED: 'gray', CANCELLED: 'gray', REVOKED: 'gray', EXPIRED: 'gray', INACTIVE: 'gray',
});

/**
 * @param {{ status: string, label: string }} props
 */
export function StatusBadge({ status, label }) {
  return html`<span class="integration-status" data-color=${STATUS_COLORS[status] ?? 'gray'}>${label}</span>`;
}

/**
 * @param {{ label: string, value: string, onInput: (value: string) => void, placeholder?: string, type?: string, hint?: string,
 *   isRequired?: boolean, multiline?: boolean, maxLength?: number, autoFocus?: boolean }} props
 */
export function TextField({ label, value, onInput, placeholder, type = 'text', hint, isRequired = false, multiline = false, maxLength, autoFocus = false }) {
  return html`<label class="form-field">
    <span class="form-field-label">${label}${isRequired ? html`<span class="form-field-required">*</span>` : null}</span>
    ${multiline
      ? html`<textarea class="text-input integration-textarea" value=${value} placeholder=${placeholder} maxLength=${maxLength}
          onInput=${(/** @type {Event} */ event) => onInput(/** @type {HTMLTextAreaElement} */ (event.currentTarget).value)}></textarea>`
      : html`<input class="text-input" type=${type} value=${value} placeholder=${placeholder} maxLength=${maxLength} autocomplete="off" autoFocus=${autoFocus}
          onInput=${(/** @type {Event} */ event) => onInput(/** @type {HTMLInputElement} */ (event.currentTarget).value)} />`}
    ${hint ? html`<span class="input-hint">${hint}</span>` : null}
  </label>`;
}

/**
 * @param {{ label: string, value: string, onChange: (value: string) => void, options: Array<{ value: string, label: string }>, hint?: string }} props
 */
export function SelectField({ label, value, onChange, options, hint }) {
  return html`<label class="form-field">
    <span class="form-field-label">${label}</span>
    <select class="text-input integration-select" value=${value} onChange=${(/** @type {Event} */ event) => onChange(/** @type {HTMLSelectElement} */ (event.currentTarget).value)}>
      ${options.map((option) => html`<option key=${option.value} value=${option.value} selected=${option.value === value}>${option.label}</option>`)}
    </select>
    ${hint ? html`<span class="input-hint">${hint}</span>` : null}
  </label>`;
}

export function AdminOnlyNote() {
  return html`<p class="channel-card-note"><${Icon} name="IconLock" size=${14} />${t('leadCapture.common.adminOnly')}</p>`;
}

/**
 * Mensagem amigável do erro; códigos sem tradução própria mostram também o detalhe da API.
 * @param {unknown} error
 */
export function describeError(error) {
  const message = getErrorMessage(error);
  if (error instanceof ApiError && error.messages[0] && (message === t('errors.UNKNOWN') || error.code === 'INVALID_INPUT')) {
    return `${message} ${error.messages[0]}`;
  }
  return message;
}

/**
 * @param {{ message: string | null }} props
 */
export function FormError({ message }) {
  if (!message) return null;
  return html`<div class="form-error" role="alert"><${Icon} name="IconAlertTriangle" size=${14} /><span>${message}</span></div>`;
}
